"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, MapPin, Globe, Navigation, Map, BookOpen, Telescope, Compass } from 'lucide-react';
import { AboutSidebar } from '@/components/about/sidebar';

export default function AstrocartographyPage() {
  return (
    <div className="min-h-screen bg-slate-950">
      <div className="flex h-screen">
        {/* Sidebar */}
        <AboutSidebar activeSection="astrology" />

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-4 py-8">
            {/* Current Section Indicator */}
            <div className="mb-4 p-3 bg-teal-500/10 border border-teal-500/20 rounded-lg">
              <div className="flex items-center gap-2 text-teal-300">
                <Telescope className="h-4 w-4" />
                <span className="text-sm font-medium">🔭 Astroloji</span>
                <span className="text-teal-500">/</span>
                <span className="text-sm">Gelişmiş Sistemler</span>
                <span className="text-teal-500">/</span>
                <span className="text-sm">Astrokartografya</span>
              </div>
            </div>

            {/* Breadcrumb */}
            <div className="mb-6">
              <Link
                href="/about"
                className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                AXTROLOG Nedir
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link
                href="/about/astrology"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Astroloji
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link
                href="/about/astrology/advanced-systems"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Gelişmiş Sistemler
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <span className="text-slate-300">Astrokartografya</span>
            </div>

            {/* Header */}
            <div className="bg-gradient-to-r from-teal-900/20 to-cyan-900/20 rounded-xl p-8 mb-8 border border-teal-500/20">
              <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
                <Globe className="h-10 w-10 text-teal-400" />
                Astrokartografya (Astrocartography)
              </h1>
              <p className="text-lg text-slate-200 leading-relaxed">
                Jim Lewis tarafından geliştirilen, natal haritadaki gezegen enerjilerinin
                dünya haritası üzerinde gösterildiği lokasyonel astroloji sistemi.
                Farklı coğrafi konumlarda gezegenlerin açısal pozisyonlarını analiz ederek
                yaşam için en uygun yerleri belirlemeye yardımcı olur.
              </p>
            </div>

            {/* Basic Concept */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Temel Kavramlar</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <MapPin className="h-5 w-5 text-teal-400" />
                      Lokasyonel Astroloji Prensibi
                    </h3>
                    <div className="space-y-3">
                      <p className="text-slate-300">
                        Aynı doğum zamanında farklı lokasyonlarda farklı ASC/MC
                        pozisyonları oluşur. Bu da gezegenlerin house pozisyonlarını değiştirir.
                      </p>
                      <div className="bg-teal-900/20 border border-teal-700/50 rounded-lg p-4">
                        <h4 className="font-semibold text-teal-200 mb-2">Temel İlke:</h4>
                        <div className="space-y-1 text-sm text-slate-300">
                          <div>• Aynı natal chart, farklı lokasyonlarda farklı manifestasyonlar</div>
                          <div>• Gezegen enerjileri coğrafi konuma göre vurgulanır</div>
                          <div>• ASC/MC çizgileri en güçlü etkiye sahiptir</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <Navigation className="h-5 w-5 text-cyan-400" />
                      Planetary Lines
                    </h3>
                    <div className="space-y-3">
                      <p className="text-slate-300">
                        Her gezegen için dört ana çizgi vardır: ASC, DSC, MC, IC.
                        Bu çizgiler üzerinde yaşanan deneyimler gezegen enerjisini yoğunlaştırır.
                      </p>
                      <div className="bg-cyan-900/20 border border-cyan-700/50 rounded-lg p-4">
                        <h4 className="font-semibold text-cyan-200 mb-2">Dört Ana Çizgi:</h4>
                        <div className="space-y-1 text-sm text-slate-300">
                          <div>• ASC Line: Kişilik ve kimlik vurgulama</div>
                          <div>• MC Line: Kariyer ve toplumsal statü</div>
                          <div>• DSC Line: İlişkiler ve ortaklıklar</div>
                          <div>• IC Line: Kök, aile, içsel güvenlik</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Jim Lewis and History */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Jim Lewis ve Tarihsel Gelişim</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-xl font-semibold text-slate-100 mb-4 text-amber-300">Jim Lewis (1941-1995)</h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Amerikalı astrolog ve yazılım geliştirici. Modern astrokartografyanın
                      kurucusu olarak bilgisayar teknolojisini astrolojiyle birleştirdi.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>"Astrocartography" terimi:</strong> 1978'de ilk kullanım</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Bilgisayar yazılımı:</strong> İlk astrokartografi programı</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Kitapları:</strong> Locational astrology üzerine öncü eserler</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-xl font-semibold text-slate-100 mb-4 text-green-300">Modern Gelişim</h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Donald Kerr, Martin Davis ve diğer astrologlar tarafından
                      sistem genişletildi ve günümüz teknolojisiyle entegre edildi.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Local Space:</strong> Donald Kerr'ın katkısı</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>A*C*G Maps:</strong> Professional astrocartography servisi</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Modern yazılımlar:</strong> Online harita oluşturma araçları</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Planetary Line Meanings */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Gezegen Çizgilerinin Anlamları</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Sun Lines */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">☉</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Güneş Çizgileri</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☉ ASC: Kişisel güç ve vitalite</div>
                      <div className="text-sm text-slate-400">Özgüven artışı, liderlik fırsatları, tanınma</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☉ MC: Kariyer başarısı</div>
                      <div className="text-sm text-slate-400">Profesyonel tanınma, otorite pozisyonları</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☉ DSC: Güçlü ortaklıklar</div>
                      <div className="text-sm text-slate-400">Etkili işbirlikleri, karizma artışı</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☉ IC: Aile ve kök bağlılığı</div>
                      <div className="text-sm text-slate-400">Ev ve family matter'larda güçlü duruş</div>
                    </div>
                  </div>
                </div>

                {/* Moon Lines */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">☽</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Ay Çizgileri</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☽ ASC: Duygusal duyarlılık</div>
                      <div className="text-sm text-slate-400">Sezgi artışı, empati, ailesel bağlar</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☽ MC: Bakım ve beslenme kariyeri</div>
                      <div className="text-sm text-slate-400">Healthcare, eğitim, hizmet sektörü</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☽ DSC: Duygusal ilişkiler</div>
                      <div className="text-sm text-slate-400">Yoğun duygusal bağlar, ailesel ortaklıklar</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☽ IC: Ev ve güvenlik</div>
                      <div className="text-sm text-slate-400">Güçlü kök hissi, aile bağları</div>
                    </div>
                  </div>
                </div>

                {/* Mercury Lines */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">☿</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Merkür Çizgileri</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☿ ASC: İletişim yeteneği</div>
                      <div className="text-sm text-slate-400">Entelektüel merak, networking, yazma</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☿ MC: İletişim kariyeri</div>
                      <div className="text-sm text-slate-400">Medya, yazıcılık, eğitim, teknoloji</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☿ DSC: Entelektüel ortaklıklar</div>
                      <div className="text-sm text-slate-400">Fikir alışverişi, öğretim ilişkileri</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">☿ IC: Aile iletişimi</div>
                      <div className="text-sm text-slate-400">Ev tabanlı işler, yazı ve araştırma</div>
                    </div>
                  </div>
                </div>

                {/* Venus Lines */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">♀</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Venüs Çizgileri</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♀ ASC: Cazibe ve güzellik</div>
                      <div className="text-sm text-slate-400">Sosyal popülerlik, estetik gelişim</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♀ MC: Sanat kariyeri</div>
                      <div className="text-sm text-slate-400">Yaratıcı endüstriler, güzellik, lüks</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♀ DSC: Romantik ilişkiler</div>
                      <div className="text-sm text-slate-400">Aşk fırsatları, harmonik ortaklıklar</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♀ IC: Güzel yaşam alanı</div>
                      <div className="text-sm text-slate-400">Estetik ev dekorasyonu, sanatsal hobi</div>
                    </div>
                  </div>
                </div>

                {/* Mars Lines */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">♂</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Mars Çizgileri</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♂ ASC: Enerji ve cesaret</div>
                      <div className="text-sm text-slate-400">Fiziksel aktivite, rekabet, liderlik</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♂ MC: Dinamik kariyer</div>
                      <div className="text-sm text-slate-400">Spor, askeriye, müteahhitlik</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♂ DSC: Tutkulu ilişkiler</div>
                      <div className="text-sm text-slate-400">Yoğun partnership'ler, rekabet</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♂ IC: Aktif ev yaşamı</div>
                      <div className="text-sm text-slate-400">Ev projeleri, aile liderliği</div>
                    </div>
                  </div>
                </div>

                {/* Jupiter Lines */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">♃</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Jüpiter Çizgileri</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♃ ASC: Genişleme ve büyüme</div>
                      <div className="text-sm text-slate-400">Şans, iyimserlik, öğrenme fırsatları</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♃ MC: Başarı ve tanınma</div>
                      <div className="text-sm text-slate-400">Yüksek pozisyonlar, öğretmenlik, hukuk</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♃ DSC: Şanslı ortaklıklar</div>
                      <div className="text-sm text-slate-400">Avantajlı işbirlikleri, mentor ilişkileri</div>
                    </div>
                    <div className="bg-slate-750 rounded-lg p-3">
                      <div className="font-medium text-slate-200 mb-1">♃ IC: Ailesel bolluk</div>
                      <div className="text-sm text-slate-400">Ev yatırımları, aile desteği</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advanced Techniques */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">İleri Teknikler</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                    <Compass className="h-5 w-5 text-purple-400" />
                    Local Space Maps
                  </h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Donald Kerr'ın geliştirdiği teknik. Gezegenlerin azimuth
                      (yönsel) konumlarını kompas yönleriyle eşleştirir.
                    </p>
                    <div className="bg-purple-900/20 border border-purple-700/50 rounded-lg p-4">
                      <h4 className="font-semibold text-purple-200 mb-2">Praktik Kullanım:</h4>
                      <div className="space-y-1 text-sm text-slate-300">
                        <div>• Her gezegen bir pusula yönünü temsil eder</div>
                        <div>• O yöndeki seyahatler gezegen enerjisini aktive eder</div>
                        <div>• Kısa mesafe ve günlük aktiviteler için ideal</div>
                        <div>• Relocation astrology ile kombine edilir</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                    <Map className="h-5 w-5 text-orange-400" />
                    Crossing Points
                  </h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      İki gezegen çizgisinin kesiştiği noktalar özel enerji
                      kombinasyonları yaratır ve güçlü lokasyonları işaret eder.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-orange-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Power Points:</strong> İki çizginin kesişim noktaları</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-orange-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Paran Maps:</strong> Aynı anda yükselen/batan gezegenler</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-orange-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Kombinasyon etkisi:</strong> İki gezegen enerjisinin birleşimi</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Practical Applications */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Pratik Uygulamalar</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 text-green-300">Relocation</h3>
                  <div className="space-y-3">
                    <p className="text-slate-300 text-sm">
                      Kalıcı taşınma kararlarında yaşam kalitesini
                      artırabilecek lokasyonları belirleme.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400">Kariyer için en uygun şehirler</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400">Aşk ve ilişki için ideal konumlar</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400">Sağlık ve iyileşme lokasyonları</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 text-blue-300">Travel Planning</h3>
                  <div className="space-y-3">
                    <p className="text-slate-300 text-sm">
                      Kısa süreli seyahatlerin zamanlaması ve
                      destinasyon seçiminde rehberlik.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-blue-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400">İş seyahatleri için uygun yerler</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-blue-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400">Tatil ve dinlence destinasyonları</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-blue-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400">Spiritual journey lokasyonları</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 text-purple-300">Business Location</h3>
                  <div className="space-y-3">
                    <p className="text-slate-300 text-sm">
                      İş yeri, ofis ve ticari faaliyet lokasyonu
                      seçiminde stratejik planlama.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400">Şirket merkezi lokasyon analizi</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400">Franchise ve şube açılış yerleri</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400">Yatırım ve emlak değerlendirmesi</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Case Studies */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Örnek Vaka Analizleri</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 text-emerald-300">Kariyer Başarısı: Jüpiter MC Line</h3>
                  <div className="space-y-3">
                    <div className="bg-emerald-900/20 border border-emerald-700/30 rounded-lg p-4">
                      <div className="font-medium text-emerald-200 mb-2">Vaka Özeti:</div>
                      <div className="text-sm text-slate-300 space-y-1">
                        <div>• Müzik prodüktörü Los Angeles'a taşındı</div>
                        <div>• Doğum yerinde Jüpiter 6. house'daydı</div>
                        <div>• LA'de Jüpiter MC line üzerinde</div>
                        <div>• 3 yıl içinde Grammy nomination aldı</div>
                      </div>
                    </div>
                    <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-4">
                      <div className="font-medium text-blue-200 mb-2">Astrolojik Analiz:</div>
                      <div className="text-sm text-slate-300 space-y-1">
                        <div>• MC Jüpiter: Expansion ve recognition</div>
                        <div>• Entertainment industry ile uyum</div>
                        <div>• Public success ve prestige artışı</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 text-rose-300">İlişki Transformasyonu: Venüs DSC Line</h3>
                  <div className="space-y-3">
                    <div className="bg-rose-900/20 border border-rose-700/30 rounded-lg p-4">
                      <div className="font-medium text-rose-200 mb-2">Vaka Özeti:</div>
                      <div className="text-sm text-slate-300 space-y-1">
                        <div>• Uzun süre single kalan kişi Paris'e seyahat</div>
                        <div>• Paris'te Venüs DSC line'a çok yakın</div>
                        <div>• İlk hafta içinde romantik ilişki başladı</div>
                        <div>• 1 yıl sonra evlilik ve Paris'e taşınma</div>
                      </div>
                    </div>
                    <div className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-4">
                      <div className="font-medium text-purple-200 mb-2">Astrolojik Faktörler:</div>
                      <div className="text-sm text-slate-300 space-y-1">
                        <div>• DSC Venüs: Partnership magnetism</div>
                        <div>• French culture ile uyumlu sanatsal enerji</div>
                        <div>• Relationship skills geliştirme fırsatı</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Study Resources */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Önerilen Kaynaklar
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-slate-200 mb-3">Temel Eserler</h4>
                  <div className="space-y-2 text-sm text-slate-400">
                    <div>• "Astrocartography" - Jim Lewis</div>
                    <div>• "The Psychology of Astro*Carto*Graphy" - Jim Lewis</div>
                    <div>• "Mapping Your Travels and Your Life" - Martin Davis</div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-200 mb-3">Modern Kaynaklar</h4>
                  <div className="space-y-2 text-sm text-slate-400">
                    <div>• "Astrology and Location" - Eric Meeus</div>
                    <div>• "Local Space & Astrocartography" - Donald Kerr</div>
                    <div>• "Relocational Astrology" - Moses Siregar III</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Note */}
            <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-red-200 mb-3">⚠️ Önemli Açıklama</h3>
              <p className="text-red-100 leading-relaxed">
                Astrokartografya kişisel tercihleri ve pratik faktörleri de göz önünde bulundurması gereken
                bir rehberlik sistemidir. Bu bilgiler sadece eğitim amaçlıdır ve hiçbiri bilimsel kesinlik iddiası taşımaz.
                Önemli yaşam kararları alırken profesyonel astrokartografi uzmanlarıyla çalışılması önerilir.
              </p>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <Link
                href="/about/astrology/advanced-systems/cosmobiology"
                className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Kozmobiyoloji
              </Link>

              <Link
                href="/about/astrology/advanced-systems"
                className="inline-flex items-center px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors"
              >
                Gelişmiş Sistemler Ana Sayfa
                <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}